import { supabase } from '../supabase';
import { 
  StockReservation, 
  CreateStockReservationRequest, 
  UpdateStockReservationRequest,
  ReservationConflictInfo,
  ReservationSummary,
  AvailabilityCheck,
  ReservationStatus,
  ReservationType
} from '../types/warehouse';
import { 
  StockReservationError,
  InsufficientStockError,
  ReservationExpiredError,
  ReservationConflictError,
  createWarehouseError
} from '../errors/warehouseErrors';
import { AuditTrailService } from './auditTrailService';
import { withTransaction } from '../utils/database';
import type { TransactionContext } from '../utils/database';
import { formatWarehouseName } from '../utils/warehouseLabel';

export class StockReservationService {
  
  /**
   * Create a new stock reservation
   */
  static async createReservation(
    request: CreateStockReservationRequest,
    auditContext: { organizationId: string; userId: string }
  ): Promise<StockReservation> {
    return withTransaction(async (context: TransactionContext) => {
      // Check availability first
      const availability = await this.checkAvailability(
        request.product_id, 
        request.zone_id, 
        request.quantity
      );
      
      if (availability.available_stock < request.quantity) {
        const conflictInfo = await this.getConflictInfo(
          request.product_id,
          request.zone_id,
          request.quantity
        );
        throw new ReservationConflictError(
          `Insufficient available stock. Requested: ${request.quantity}, Available: ${availability.available_stock}`,
          conflictInfo
        );
      }

      // Set default expires_at if not provided and has default duration
      let expiresAt = request.expires_at;
      if (!expiresAt && request.reservation_type !== 'manual') {
        const defaultHours = 24; // TODO: Get from config
        expiresAt = new Date(Date.now() + defaultHours * 60 * 60 * 1000).toISOString();
      }

      // Create reservation
      const reservationData = {
        organization_id: auditContext.organizationId,
        product_id: request.product_id,
        zone_id: request.zone_id,
        quantity: request.quantity,
        reservation_type: request.reservation_type,
        priority: request.priority || 0,
        reference_type: request.reference_type,
        reference_id: request.reference_id,
        expires_at: expiresAt,
        reserved_until: request.reserved_until,
        reserved_by: auditContext.userId,
        notes: request.notes,
        metadata: request.metadata || {}
      };

      const { data: reservation, error } = await context.client
        .from('stock_reservations')
        .insert([reservationData])
        .select(`
          *,
          product:products(id, name, sku),
          zone:warehouse_zones(id, name, code),
          reserved_by_profile:user_profiles!reserved_by(id, full_name, email)
        `)
        .single();

      if (error) {
        throw createWarehouseError(error, 'create stock reservation');
      }

      // Log the operation
      await AuditTrailService.logOperation(
        auditContext,
        'CREATE',
        'stock_reservation',
        reservation.id,
        {
          entityName: `${reservation.product?.name} - ${reservation.zone?.name}`,
          changes: {
            quantity: { from: null, to: request.quantity },
            reservation_type: { from: null, to: request.reservation_type },
            expires_at: { from: null, to: expiresAt }
          },
          metadata: {
            product_id: request.product_id,
            zone_id: request.zone_id,
            reference_type: request.reference_type,
            reference_id: request.reference_id
          }
        }
      );

      return reservation;
    });
  }

  /**
   * Update an existing reservation
   */
  static async updateReservation(
    reservationId: string,
    updates: UpdateStockReservationRequest,
    auditContext: { organizationId: string; userId: string }
  ): Promise<StockReservation> {
    return withTransaction(async (context: TransactionContext) => {
      // Get current reservation
      const { data: currentReservation, error: fetchError } = await context.client
        .from('stock_reservations')
        .select('*')
        .eq('id', reservationId)
        .eq('organization_id', auditContext.organizationId)
        .single();

      if (fetchError || !currentReservation) {
        throw new StockReservationError(`Reservation not found: ${reservationId}`);
      }

      if (currentReservation.status !== 'active') {
        throw new StockReservationError(`Cannot update reservation with status: ${currentReservation.status}`);
      }

      // If quantity is being changed, check availability
      if (updates.quantity && updates.quantity !== currentReservation.quantity) {
        const quantityDifference = updates.quantity - currentReservation.quantity;
        if (quantityDifference > 0) {
          // Increasing quantity - check if we have enough available
          const availability = await this.checkAvailability(
            currentReservation.product_id,
            currentReservation.zone_id,
            quantityDifference
          );
          
          if (availability.available_stock < quantityDifference) {
            throw new InsufficientStockError(
              quantityDifference,
              availability.available_stock,
              'Cannot increase reservation quantity'
            );
          }
        }
      }

      // Update reservation
      const { data: updatedReservation, error: updateError } = await context.client
        .from('stock_reservations')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', reservationId)
        .eq('organization_id', auditContext.organizationId)
        .select(`
          *,
          product:products(id, name, sku),
          zone:warehouse_zones(id, name, code),
          reserved_by_profile:user_profiles!reserved_by(id, full_name, email)
        `)
        .single();

      if (updateError) {
        throw createWarehouseError(updateError, 'update stock reservation');
      }

      // Log the changes
      const changes: Record<string, { from: unknown; to: unknown }> = {};
      for (const [key, newValue] of Object.entries(updates)) {
        const oldValue = currentReservation[key];
        if (oldValue !== newValue) {
          changes[key] = { from: oldValue, to: newValue };
        }
      }

      if (Object.keys(changes).length > 0) {
        await AuditTrailService.logOperation(
          auditContext,
          'UPDATE',
          'stock_reservation',
          reservationId,
          {
            entityName: `${updatedReservation.product?.name} - ${updatedReservation.zone?.name}`,
            changes,
            metadata: {
              product_id: currentReservation.product_id,
              zone_id: currentReservation.zone_id
            }
          }
        );
      }

      return updatedReservation;
    });
  }

  /**
   * Cancel a reservation
   */
  static async cancelReservation(
    reservationId: string,
    reason: string,
    auditContext: { organizationId: string; userId: string }
  ): Promise<StockReservation> {
    return withTransaction(async (context: TransactionContext) => {
      const { data: reservation, error } = await context.client
        .from('stock_reservations')
        .update({
          status: 'cancelled',
          cancelled_by: auditContext.userId,
          cancelled_at: new Date().toISOString(),
          notes: reason,
          updated_at: new Date().toISOString()
        })
        .eq('id', reservationId)
        .eq('organization_id', auditContext.organizationId)
        .eq('status', 'active') // Can only cancel active reservations
        .select(`
          *,
          product:products(id, name, sku),
          zone:warehouse_zones(id, name, code)
        `)
        .single();

      if (error) {
        throw createWarehouseError(error, 'cancel stock reservation');
      }

      if (!reservation) {
        throw new StockReservationError('Reservation not found or already cancelled');
      }

      // Log the cancellation
      await AuditTrailService.logOperation(
        auditContext,
        'CANCEL',
        'stock_reservation',
        reservationId,
        {
          entityName: `${reservation.product?.name} - ${reservation.zone?.name}`,
          changes: {
            status: { from: 'active', to: 'cancelled' },
            cancelled_reason: { from: null, to: reason }
          },
          metadata: {
            product_id: reservation.product_id,
            zone_id: reservation.zone_id,
            quantity: reservation.quantity
          }
        }
      );

      return reservation;
    });
  }

  /**
   * Fulfill a reservation (mark as used and remove from reserved stock)
   */
  static async fulfillReservation(
    reservationId: string,
    actualQuantityUsed?: number,
    auditContext: { organizationId: string; userId: string }
  ): Promise<StockReservation> {
    return withTransaction(async (context: TransactionContext) => {
      // Get current reservation
      const { data: currentReservation, error: fetchError } = await context.client
        .from('stock_reservations')
        .select('*')
        .eq('id', reservationId)
        .eq('organization_id', auditContext.organizationId)
        .single();

      if (fetchError || !currentReservation) {
        throw new StockReservationError(`Reservation not found: ${reservationId}`);
      }

      if (currentReservation.status !== 'active') {
        throw new StockReservationError(`Cannot fulfill reservation with status: ${currentReservation.status}`);
      }

      // Check if reservation has expired
      if (currentReservation.expires_at && new Date(currentReservation.expires_at) < new Date()) {
        throw new ReservationExpiredError(`Reservation expired at ${currentReservation.expires_at}`);
      }

      const quantityUsed = actualQuantityUsed || currentReservation.quantity;

      // Update reservation to fulfilled
      const { data: reservation, error } = await context.client
        .from('stock_reservations')
        .update({
          status: 'fulfilled',
          fulfilled_by: auditContext.userId,
          fulfilled_at: new Date().toISOString(),
          quantity: quantityUsed, // Update to actual quantity used
          updated_at: new Date().toISOString()
        })
        .eq('id', reservationId)
        .eq('organization_id', auditContext.organizationId)
        .select(`
          *,
          product:products(id, name, sku),
          zone:warehouse_zones(id, name, code)
        `)
        .single();

      if (error) {
        throw createWarehouseError(error, 'fulfill stock reservation');
      }

      // Log the fulfillment
      await AuditTrailService.logOperation(
        auditContext,
        'FULFILL',
        'stock_reservation',
        reservationId,
        {
          entityName: `${reservation.product?.name} - ${reservation.zone?.name}`,
          changes: {
            status: { from: 'active', to: 'fulfilled' },
            quantity_used: { from: currentReservation.quantity, to: quantityUsed }
          },
          metadata: {
            product_id: reservation.product_id,
            zone_id: reservation.zone_id,
            original_quantity: currentReservation.quantity,
            actual_quantity: quantityUsed
          }
        }
      );

      return reservation;
    });
  }

  /**
   * Check stock availability for reservation
   */
  static async checkAvailability(
    productId: string,
    zoneId: string,
    requestedQuantity: number
  ): Promise<AvailabilityCheck> {
    // La RPC `get_zone_availability` non e' mai esistita in produzione: ogni chiamata
    // rispondeva PGRST202 e questo metodo lanciava. Siccome createReservation lo invoca
    // per primo, nessuna prenotazione e' mai stata creata da qui — in produzione
    // 41 order_items, zero con reservation_id.
    //
    // La RPC `get_available_stock(zone_uuid, product_uuid)` che esiste in produzione NON
    // e' equivalente e non va usata come sostituto: prende la giacenza da
    // `warehouse_zones.current_usage`, che e' l'occupazione complessiva della zona per
    // tutti i prodotti, non la giacenza del singolo prodotto.
    //
    // Le due grandezze si leggono direttamente: `product_zone_storage` per la giacenza
    // di quel prodotto in quella zona, e le prenotazioni attive per la parte impegnata.
    const { data: zoneStorage, error: storageError } = await supabase
      .from('product_zone_storage')
      .select('quantity')
      .eq('product_id', productId)
      .eq('zone_id', zoneId);

    if (storageError) {
      throw createWarehouseError(storageError, 'fetch zone stock');
    }

    // Get existing reservations for context
    const { data: reservations, error: reservationsError } = await supabase
      .from('stock_reservations')
      .select(`
        *,
        product:products(name, sku),
        reserved_by_profile:user_profiles!reserved_by(full_name, email)
      `)
      .eq('product_id', productId)
      .eq('zone_id', zoneId)
      .eq('status', 'active')
      .order('priority', { ascending: false })
      .order('reserved_at', { ascending: true });

    if (reservationsError) {
      throw createWarehouseError(reservationsError, 'fetch existing reservations');
    }

    const totalStock = (zoneStorage || []).reduce(
      (sum, row) => sum + (row.quantity || 0),
      0
    );
    // Lo stesso elenco che torniamo in `existing_reservations`: cosi' il totale
    // impegnato e il dettaglio mostrato non possono divergere.
    const reservedStock = (reservations || []).reduce(
      (sum, r: { quantity?: number | null }) => sum + (r.quantity || 0),
      0
    );
    const availableStock = totalStock - reservedStock;

    return {
      product_id: productId,
      zone_id: zoneId,
      total_stock: totalStock,
      reserved_stock: reservedStock,
      available_stock: availableStock,
      can_reserve_quantity: Math.max(0, availableStock),
      existing_reservations: reservations || []
    };
  }

  /**
   * Get detailed conflict information when reservation fails
   */
  static async getConflictInfo(
    productId: string,
    zoneId: string,
    requestedQuantity: number
  ): Promise<ReservationConflictInfo> {
    const availability = await this.checkAvailability(productId, zoneId, requestedQuantity);

    // Get zone info
    const { data: zone, error: zoneError } = await supabase
      .from('warehouse_zones')
      .select('id, name, code')
      .eq('id', zoneId)
      .single();

    if (zoneError) {
      throw createWarehouseError(zoneError, 'fetch zone info');
    }

    // Find alternative zones with the product
    const { data: alternativeZones, error: altError } = await supabase
      .from('warehouse_zones')
      .select(`
        id, name, code,
        current_usage,
        reserved_quantity
      `)
      .eq('organization_id', zone.organization_id)
      .eq('is_active', true)
      .neq('id', zoneId)
      .gt('current_usage', 0); // Only zones that have stock

    if (altError) {
      throw createWarehouseError(altError, 'fetch alternative zones');
    }

    const alternatives = (alternativeZones || []).map(altZone => ({
      zone_id: altZone.id,
      zone_name: formatWarehouseName(altZone),
      available_quantity: Math.max(0, altZone.current_usage - altZone.reserved_quantity)
    })).filter(alt => alt.available_quantity >= requestedQuantity);

    // Check when stock might become available (look at reservation expiration)
    const { data: expiringReservations } = await supabase
      .from('stock_reservations')
      .select('expires_at, quantity')
      .eq('product_id', productId)
      .eq('zone_id', zoneId)
      .eq('status', 'active')
      .not('expires_at', 'is', null)
      .order('expires_at', { ascending: true })
      .limit(5);

    let canWaitUntil: string | undefined;
    let cumulativeReleasing = 0;
    for (const res of expiringReservations || []) {
      cumulativeReleasing += res.quantity;
      if (availability.available_stock + cumulativeReleasing >= requestedQuantity) {
        canWaitUntil = res.expires_at;
        break;
      }
    }

    return {
      zone_id: zoneId,
      zone_name: formatWarehouseName(zone),
      requested_quantity: requestedQuantity,
      available_quantity: availability.available_stock,
      conflicting_reservations: availability.existing_reservations,
      total_reserved: availability.reserved_stock,
      suggestions: {
        alternative_zones: alternatives,
        can_wait_until: canWaitUntil,
        can_partially_fulfill: availability.available_stock > 0 ? availability.available_stock : undefined
      }
    };
  }

  /**
   * Get reservation summary/statistics
   */
  static async getReservationSummary(
    organizationId: string,
    filters?: {
      dateRange?: { from: string; to: string };
      productId?: string;
      zoneId?: string;
      reservationType?: ReservationType;
    }
  ): Promise<ReservationSummary> {
    let query = supabase
      .from('stock_reservations')
      .select('*')
      .eq('organization_id', organizationId);

    // Apply filters
    if (filters?.dateRange) {
      query = query
        .gte('reserved_at', filters.dateRange.from)
        .lte('reserved_at', filters.dateRange.to);
    }
    if (filters?.productId) {
      query = query.eq('product_id', filters.productId);
    }
    if (filters?.zoneId) {
      query = query.eq('zone_id', filters.zoneId);
    }
    if (filters?.reservationType) {
      query = query.eq('reservation_type', filters.reservationType);
    }

    const { data: reservations, error } = await query;

    if (error) {
      throw createWarehouseError(error, 'fetch reservation summary');
    }

    // Calculate statistics
    const summary = {
      total_reservations: reservations.length,
      active_reservations: reservations.filter(r => r.status === 'active').length,
      expired_reservations: reservations.filter(r => r.status === 'expired').length,
      fulfilled_reservations: reservations.filter(r => r.status === 'fulfilled').length,
      cancelled_reservations: reservations.filter(r => r.status === 'cancelled').length,
      total_reserved_quantity: reservations
        .filter(r => r.status === 'active')
        .reduce((sum, r) => sum + r.quantity, 0),
      reservations_by_type: {} as Record<ReservationType, number>,
      expiring_soon: [] as StockReservation[]
    };

    // Count by type
    const types: ReservationType[] = ['order', 'maintenance', 'transfer', 'rental', 'manual'];
    for (const type of types) {
      summary.reservations_by_type[type] = reservations.filter(r => r.reservation_type === type).length;
    }

    // Find expiring soon (next 24 hours)
    const next24Hours = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    summary.expiring_soon = reservations.filter(r => 
      r.status === 'active' && 
      r.expires_at && 
      r.expires_at <= next24Hours
    );

    return summary;
  }

  /**
   * Expire old reservations (to be called periodically)
   */
  static async expireReservations(organizationId: string): Promise<number> {
    const { data, error } = await supabase.rpc('expire_stock_reservations');

    if (error) {
      throw createWarehouseError(error, 'expire stock reservations');
    }

    return data || 0;
  }

  /**
   * Get reservations for a product/zone
   */
  static async getReservations(
    organizationId: string,
    filters: {
      productId?: string;
      zoneId?: string;
      status?: ReservationStatus[];
      reservationType?: ReservationType[];
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<{ reservations: StockReservation[]; total: number }> {
    let query = supabase
      .from('stock_reservations')
      .select(`
        *,
        product:products(id, name, sku),
        zone:warehouse_zones(id, name, code),
        reserved_by_profile:user_profiles!reserved_by(id, full_name, email)
      `, { count: 'exact' })
      .eq('organization_id', organizationId)
      .order('reserved_at', { ascending: false });

    if (filters.productId) {
      query = query.eq('product_id', filters.productId);
    }
    if (filters.zoneId) {
      query = query.eq('zone_id', filters.zoneId);
    }
    if (filters.status) {
      query = query.in('status', filters.status);
    }
    if (filters.reservationType) {
      query = query.in('reservation_type', filters.reservationType);
    }
    if (filters.limit) {
      query = query.limit(filters.limit);
    }
    if (filters.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
    }

    const { data: reservations, error, count } = await query;

    if (error) {
      throw createWarehouseError(error, 'fetch reservations');
    }

    return {
      reservations: reservations || [],
      total: count || 0
    };
  }
}